/* eslint-disable no-nested-ternary */
import {
  Breadcrumbs,
  Modal,
  RankModuleLayout,
  SignInForm,
  SignUpForm,
} from '@components';
import {
  useNavigateWithOfflineMode,
  useScreenSize,
  useScrollToTop,
} from '@hooks';
import Net from '@pngs/OnboardingPage/Net.png';
import { authSlice, userApi } from '@store';
import { Button, Flex, theme, Typography } from '@styles';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import RegisterToContinueModal from '../../components/RegisterToContinueModal/RegisterToContinueModal';
import {
  onboardingPageLayoutByVariant,
  useTextVariant,
} from './OnboardingPage.layoutProps';

const OnboardingPage = () => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const { navigate } = useNavigateWithOfflineMode();
  const { device, isMobile, isTabletLG, isTabletSM } = useScreenSize();

  const [isRankModalVisible, setRankModalVisible] = useState(false);
  const [rankModalContent, setRankModalContent] = useState(null);
  const [
    isRegisterToContinueModalVisible,
    setIsRegisterToContinueModalVisible,
  ] = useState(false);
  const [authModalState, setAuthModalState] = useState(null);

  const layout = onboardingPageLayoutByVariant[device || 'desktop'];
  const [currentSlide, setCurrentSlide] = useState(0);

  const { isOnboardingPassed, jwtAuthenticationToken, userId } = useSelector(
    (state) => state.authSlice
  );
  const [finishOnboarding] = userApi.useUpdateOnboardingProgressMutation();

  const textVariant = useTextVariant({ currentSlide });

  const points = i18n.t('OnboardingPage.Points', {
    returnObjects: true,
  });

  const goBack = () => {
    navigate(-1);
  };

  const onPressNext = () => setCurrentSlide((prevState) => prevState + 1);
  const onPressPrevious = () => setCurrentSlide((prevState) => prevState - 1);
  const onPressFinish = async () => {
    if (!jwtAuthenticationToken) {
      setIsRegisterToContinueModalVisible(true);
      return;
    }

    const response = await finishOnboarding({ userId });
    if (!isOnboardingPassed && response?.data?.data?.new_rank) {
      setRankModalContent(response?.data?.data?.new_rank);
      setRankModalVisible(true);
    }
    if (!isOnboardingPassed) {
      dispatch(authSlice.actions.setOnboardingStatus(true));
    }
    if (isOnboardingPassed) {
      goBack();
    }
  };

  const handleAuthModalStateChange = (state) => {
    setAuthModalState(state);
  };

  const handleAuthModalClose = () => {
    setAuthModalState(null);
  };

  const modalLayoutByState = {
    signIn: <SignInForm onSignInButtonClick={handleAuthModalClose} />,
    signUp: <SignUpForm onSignUpButtonClick={handleAuthModalClose} />,
  };

  useScrollToTop();

  return (
    <Flex flexDirection="column">
      <RegisterToContinueModal
        isVisible={isRegisterToContinueModalVisible}
        onClose={handleAuthModalStateChange}
        setModalVisible={setIsRegisterToContinueModalVisible}
      />
      <Modal
        isVisible={!!authModalState}
        setModalVisible={(state) => setAuthModalState(state ? 'signIn' : null)}
        withBackdropNoise
      >
        {modalLayoutByState[authModalState]}
      </Modal>
      <RankModuleLayout
        isVisible={isRankModalVisible}
        onClose={goBack}
        rank={rankModalContent}
        setIsVisible={setRankModalVisible}
      />
      <Breadcrumbs containerProps={{ px: layout?.px }} />
      <Flex
        backgroundImage={Net}
        backgroundSize="contain"
        pb={80}
        pt={24}
        px={layout.px}
      >
        <Flex bg={theme.colors.black[20]} flexDirection="column" width="100%">
          <Flex
            alignItems="center"
            flexDirection="column"
            height={layout.height}
            justifyContent="center"
            position="relative"
            px={isTabletSM ? 16 : isTabletLG ? 80 : 120}
          >
            <Flex
              justifyContent="space-between"
              left={0}
              position="absolute"
              top={0}
              width="100%"
              zIndex={5}
            >
              {currentSlide === 0 ? (
                <Flex bg={theme.colors.white[5]} width={200} />
              ) : (
                <Button
                  bg={theme.colors.white[5]}
                  border={0}
                  borderRadius={0}
                  maxWidth={200}
                  onClick={onPressPrevious}
                  variant="quaternary"
                  width={isMobile ? '50%' : '100%'}
                >
                  <Typography
                    color={theme.colors.white[100]}
                    variant="MS-bold-s16-lh25"
                  >
                    {'<< '}
                    {i18n.t('OnboardingPage.Previous')}
                  </Typography>
                </Button>
              )}

              {currentSlide !== points.length - 1 ? (
                <Button
                  bg={theme.colors.white[5]}
                  border={0}
                  borderRadius={0}
                  onClick={onPressNext}
                  variant="quaternary"
                  width={200}
                >
                  <Typography
                    color={theme.colors.white[100]}
                    variant="MS-bold-s16-lh25"
                  >
                    {i18n.t('OnboardingPage.Next')}
                    {' >>'}
                  </Typography>
                </Button>
              ) : (
                <Button
                  borderRadius={0}
                  onClick={onPressFinish}
                  variant="primary"
                  width={200}
                >
                  <Typography
                    color={theme.colors.background}
                    variant="MS-bold-s16-lh25"
                  >
                    {i18n.t('OnboardingPage.Finish')}
                    {' >>'}
                  </Typography>
                </Button>
              )}
            </Flex>
            <SlideIndex
              color={theme.colors.hunterGreen[100]}
              position="absolute"
              right={10}
              top={70}
              variant="BN-bold-s318-lh333"
              zIndex={4}
            >
              {currentSlide}
            </SlideIndex>
            <Typography
              color={theme.colors.white[100]}
              mt={isTabletLG ? 48 : 20}
              variant={textVariant}
            >
              {points?.[currentSlide]}
            </Typography>
          </Flex>
          {/* <Flex bg={theme.colors.white[5]} justifyContent="space-between">
            {currentSlide === 0 ? (
              <Flex bg={theme.colors.white[5]} width={200} />
            ) : (
              <Button
                bg={theme.colors.white[5]}
                border={0}
                borderRadius={0}
                maxWidth={200}
                onClick={onPressPrevious}
                variant="quaternary"
                width={isMobile ? '50%' : '100%'}
              >
                <Typography
                  color={theme.colors.white[100]}
                  variant="MS-bold-s16-lh25"
                >
                  {'<< '}
                  {i18n.t('OnboardingPage.Previous')}
                </Typography>
              </Button>
            )}

            {currentSlide !== points.length - 1 ? (
              <Button
                bg={theme.colors.white[5]}
                border={0}
                borderRadius={0}
                onClick={onPressNext}
                variant="quaternary"
                width={200}
              >
                <Typography
                  color={theme.colors.white[100]}
                  variant="MS-bold-s16-lh25"
                >
                  {i18n.t('OnboardingPage.Next')}
                  {' >>'}
                </Typography>
              </Button>
            ) : jwtAuthenticationToken ? (
              <Button
                borderRadius={0}
                onClick={onPressFinish}
                variant="primary"
                width={200}
              >
                <Typography
                  color={theme.colors.background}
                  variant="MS-bold-s16-lh25"
                >
                  {i18n.t('OnboardingPage.Finish')}
                  {' >>'}
                </Typography>
              </Button>
            ) : (
              <Flex bg={theme.colors.white[5]} width={200} />
            )}
          </Flex> */}
        </Flex>
      </Flex>
    </Flex>
  );
};

const SlideIndex = styled(Typography)`
  mix-blend-mode: color-dodge;
`;

export default OnboardingPage;
